import { DatePipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormRecord, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IServerResponse } from 'src/app/model/add-view-call-flow.model';
import { NotificationService } from '@notification/service';
import { EMPTY_GUID_VALUE } from '@properties/application.properties';
import { LocalStorageService } from '@services/local-storage.service';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsCheckboxComponent } from '@design-system/ds-checkbox/component/ds-checkbox.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsIconComponent } from '@design-system/ds-icon/component/ds-icon.component';
import { DsTitleComponent } from '@design-system/ds-title/component/ds-title.component';
import { CheckListService } from 'src/app/MaterialDialogs/check-list/check-list-service.service';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import { CheckListDetailsDataInterface } from '@modals/check-list-details/interfaces/check-list-details-data.interface';
import { CheckListDetailsModalDataInterface } from '@modals/check-list-details/interfaces/check-list-details-modal-data.interface';
import { CheckListDetailsSavedItemInterface } from '@modals/check-list-details/interfaces/check-list-details-saved-item.interface';
import { CheckListDetailsTableRowInterface } from '@modals/check-list-details/interfaces/check-list-details-table-row.interface';
import { CheckListDetailsUpdateItemInterface } from '@modals/check-list-details/interfaces/check-list-details-update-item.interface';
import { isCheckListDetailsEditable } from '@modals/check-list-details/helpers/is-check-list-details-editable.helper';
import { buildCheckListDetailsUpdateItem } from '@modals/check-list-details/helpers/build-check-list-details-update-item.helper';
import { getCheckListDetailsItemValuesFromRow } from '@modals/check-list-details/helpers/get-check-list-details-item-values-from-row.helper';
import { getCheckListDetailsTableSortValue } from '@modals/check-list-details/helpers/get-check-list-details-table-sort-value.helper';
import { mapCheckListDetailsSavedItemToTableRow } from '@modals/check-list-details/helpers/map-check-list-details-saved-item-to-table-row.helper';
import {
  CHECK_LIST_DETAILS_DATE_FORMAT,
  CHECK_LIST_DETAILS_DISPLAYED_COLUMNS,
  CHECK_LIST_DETAILS_SPINNER_DIAMETER_PX,
  CHECK_LIST_DETAILS_SUCCESS_TIMEOUT_MS,
  CHECK_LIST_DETAILS_WARNING_TIMEOUT_MS,
} from '@modals/check-list-details/properties/check-list-details-modal.properties';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';

/**
 * V2 design-system Checklist Details modal opened via `DsModalService`.
 */
@Component({
  selector: 'app-check-list-details-modal',
  standalone: true,
  imports: [
    NgIf,
    DatePipe,
    ReactiveFormsModule,
    MatSortModule,
    MatTableModule,
    MatProgressSpinnerModule,
    DsButtonComponent,
    DsCheckboxComponent,
    DsIconButtonComponent,
    DsIconComponent,
    DsTitleComponent,
    DsTextComponent,
  ],
  templateUrl: './check-list-details-modal.component.html',
  styleUrls: ['./check-list-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckListDetailsModalComponent implements OnInit, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly checkListService = inject(CheckListService);

  private readonly notificationService = inject(NotificationService);

  private readonly localStorageService = inject(LocalStorageService);

  private readonly dialogRef = inject(MatDialogRef<CheckListDetailsModalComponent, void>);

  private readonly dialogData = inject<CheckListDetailsModalDataInterface>(MAT_DIALOG_DATA);

  private readonly destroy$ = new Subject<void>();

  private readonly currentUser: ILoggedInUserViewModel | null = this.localStorageService.getCurrentUserDetails();

  readonly dateFormat = CHECK_LIST_DETAILS_DATE_FORMAT;

  readonly spinnerDiameter = CHECK_LIST_DETAILS_SPINNER_DIAMETER_PX;

  readonly displayedColumns: string[] = [...CHECK_LIST_DETAILS_DISPLAYED_COLUMNS];

  readonly canEdit: boolean = isCheckListDetailsEditable(this.currentUser?.role);

  readonly dataSource = new MatTableDataSource<CheckListDetailsTableRowInterface>([]);

  readonly checkListForm = new FormRecord<FormControl<boolean>>({});

  pendingUpdates: CheckListDetailsUpdateItemInterface[] = [];

  isLoading = true;

  hasRows = false;

  hasPendingChanges = false;

  /**
   * Connects Material sort to the table data source when the table is rendered.
   *
   * @param sort - Material sort directive instance
   */
  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    this.dataSource.sort = sort ?? null;
  }

  /**
   * Loads checklist rows for the current service request.
   */
  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = getCheckListDetailsTableSortValue;
    this.loadCheckListDetails();
  }

  /**
   * Completes outstanding subscriptions.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Closes the modal without saving.
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Tracks toggled rows so Save posts only items that differ from the loaded state.
   *
   * @param row - checklist table row whose completed state changed
   */
  onCompletedChange(row: CheckListDetailsTableRowInterface): void {
    const itemName: string = row.itemName;
    const itemValues = getCheckListDetailsItemValuesFromRow(row);
    const existingIndex: number = this.pendingUpdates.findIndex((item: CheckListDetailsUpdateItemInterface): boolean => {
      return item.name === itemName;
    });

    if (existingIndex === -1) {
      this.pendingUpdates = [
        ...this.pendingUpdates,
        buildCheckListDetailsUpdateItem(itemName, itemValues, this.currentUser?.memberId ?? '', this.currentUser?.name ?? ''),
      ];
      row.isCheckedControl.markAsDirty();
    } else {
      this.pendingUpdates = this.pendingUpdates.filter((_item: CheckListDetailsUpdateItemInterface, itemIndex: number): boolean => {
        return itemIndex !== existingIndex;
      });
      row.isCheckedControl.markAsPristine();
      row.isCheckedControl.markAsUntouched();
    }

    this.syncPendingChangesState();
  }

  /**
   * Saves toggled checklist rows, then reloads the latest values.
   */
  onSave(): void {
    if (this.pendingUpdates.length === 0) {
      this.notificationService.warning('Please update some values to save changes!', 'Warning', CHECK_LIST_DETAILS_WARNING_TIMEOUT_MS);

      return;
    }

    this.loadCheckListDetails(this.pendingUpdates);
  }

  /**
   * Stable `trackBy` for checklist table rows.
   *
   * @param index - row index
   * @param row - table row
   */
  trackByItemName(index: number, row: CheckListDetailsTableRowInterface): string {
    return row.itemName || `check-list-row-${index}`;
  }

  /**
   * Loads or updates checklist rows for the current service request.
   *
   * @param updatedItems - pending updates to persist; omit to load only
   */
  private loadCheckListDetails(updatedItems?: CheckListDetailsUpdateItemInterface[]): void {
    const lastUpdatedById: string = updatedItems ? (this.currentUser?.memberId ?? EMPTY_GUID_VALUE) : EMPTY_GUID_VALUE;

    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    this.checkListService
      .getCheckListBySR(this.dialogData, updatedItems ?? [], lastUpdatedById)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: IServerResponse): void => {
          const checkListData: CheckListDetailsDataInterface | null = response.data ?? null;

          if (checkListData != null) {
            if (updatedItems) {
              this.notificationService.success(
                'CheckList Updated Successfully.',
                'Success',
                undefined,
                undefined,
                CHECK_LIST_DETAILS_SUCCESS_TIMEOUT_MS,
              );
            }

            this.setTableRows(checkListData.checkListItemForSpecifiedServiceType ?? []);
          }

          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.notificationService.warning(
            'Something went wrong while getting cheklist!',
            'Warning',
            CHECK_LIST_DETAILS_WARNING_TIMEOUT_MS,
          );
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  /**
   * Rebuilds the table data source from saved checklist rows.
   *
   * @param savedItems - items for the current service request
   */
  private setTableRows(savedItems: CheckListDetailsSavedItemInterface[]): void {
    const rows: CheckListDetailsTableRowInterface[] = savedItems.map(
      (savedItem: CheckListDetailsSavedItemInterface): CheckListDetailsTableRowInterface => {
        return mapCheckListDetailsSavedItemToTableRow(savedItem, this.canEdit);
      },
    );

    this.dataSource.data = rows;
    this.hasRows = rows.length > 0;
    this.rebuildCheckListForm(rows);
    this.pendingUpdates = [];
    this.syncPendingChangesState();
  }

  /**
   * Rebuilds the checklist form from table row checkbox controls and marks it pristine.
   *
   * @param rows - current table rows
   */
  private rebuildCheckListForm(rows: CheckListDetailsTableRowInterface[]): void {
    Object.keys(this.checkListForm.controls).forEach((controlName: string): void => {
      this.checkListForm.removeControl(controlName);
    });

    rows.forEach((row: CheckListDetailsTableRowInterface): void => {
      this.checkListForm.addControl(row.itemName, row.isCheckedControl);
    });

    this.checkListForm.markAsPristine();
    this.checkListForm.markAsUntouched();
  }

  /**
   * Updates whether Save is available after a checkbox toggle or a reload.
   */
  private syncPendingChangesState(): void {
    this.hasPendingChanges = this.pendingUpdates.length > 0;
    this.changeDetectorRef.markForCheck();
  }
}
